import { getFormProps, getInputProps, useForm } from '@conform-to/react'
import { getZodConstraint, parseWithZod } from '@conform-to/zod'
import { type SEOHandle } from '@nasa-gcn/remix-seo'
import * as E from '@react-email/components'
import { data, redirect, Link, useFetcher } from 'react-router'
import { HoneypotInputs } from 'remix-utils/honeypot/react'
import { z } from 'zod'
import { GeneralErrorBoundary } from '#app/components/error-boundary.tsx'
import { BrandPanel } from '#app/components/auth/panels/brand-panel.tsx'
import { SplitAuthLayout } from '#app/components/auth/split-auth-layout.tsx'
import { ErrorList, Field } from '#app/components/forms.tsx'
import { StatusButton } from '#app/components/ui/status-button.tsx'
import { prisma } from '#app/utils/db.server.ts'
import { sendEmail } from '#app/utils/email.server.ts'
import { checkHoneypot } from '#app/utils/honeypot.server.ts'
import { EmailSchema, UsernameSchema } from '#app/utils/user-validation.ts'
import { type Route } from './+types/forgot-password.ts'
import { prepareVerification } from './verify.server.ts'

export const handle: SEOHandle = {
	getSitemapEntries: () => null,
}

const ForgotPasswordSchema = z.object({
	usernameOrEmail: z.union([EmailSchema, UsernameSchema]),
})

export async function action({ request }: Route.ActionArgs) {
	const formData = await request.formData()
	await checkHoneypot(formData)
	const submission = await parseWithZod(formData, {
		schema: ForgotPasswordSchema.superRefine(async (data, ctx) => {
			const user = await prisma.user.findFirst({
				where: {
					OR: [
						{ email: data.usernameOrEmail },
						{ username: data.usernameOrEmail },
					],
				},
				select: { id: true },
			})
			if (!user) {
				ctx.addIssue({
					path: ['usernameOrEmail'],
					code: z.ZodIssueCode.custom,
					message: 'Er bestaat geen account met deze gebruikersnaam of e-mailadres',
				})
				return
			}
		}),
		async: true,
	})
	if (submission.status !== 'success') {
		return data(
			{ result: submission.reply() },
			{ status: submission.status === 'error' ? 400 : 200 },
		)
	}
	const { usernameOrEmail } = submission.value

	const user = await prisma.user.findFirstOrThrow({
		where: { OR: [{ email: usernameOrEmail }, { username: usernameOrEmail }] },
		select: { email: true, username: true },
	})

	const { verifyUrl, redirectTo, otp } = await prepareVerification({
		period: 10 * 60,
		request,
		type: 'reset-password',
		target: usernameOrEmail,
	})

	const response = await sendEmail({
		to: user.email,
		subject: `Muziekdocent — wachtwoord herstellen`,
		react: (
			<ForgotPasswordEmail onboardingUrl={verifyUrl.toString()} otp={otp} />
		),
	})

	if (response.status === 'success') {
		return redirect(redirectTo.toString())
	} else {
		return data(
			{ result: submission.reply({ formErrors: [response.error.message] }) },
			{ status: 500 },
		)
	}
}

function ForgotPasswordEmail({
	onboardingUrl,
	otp,
}: {
	onboardingUrl: string
	otp: string
}) {
	return (
		<E.Html lang="nl" dir="ltr">
			<E.Container>
				<h1>
					<E.Text>Muziekdocent — wachtwoord herstellen</E.Text>
				</h1>
				<p>
					<E.Text>
						Je verificatiecode is: <strong>{otp}</strong>
					</E.Text>
				</p>
				<p>
					<E.Text>Of klik op de link:</E.Text>
				</p>
				<E.Link href={onboardingUrl}>{onboardingUrl}</E.Link>
			</E.Container>
		</E.Html>
	)
}

export const meta: Route.MetaFunction = () => {
	return [{ title: 'Wachtwoord vergeten | Muziekdocent' }]
}

export default function ForgotPasswordRoute() {
	const forgotPassword = useFetcher<typeof action>()

	const [form, fields] = useForm({
		id: 'forgot-password-form',
		constraint: getZodConstraint(ForgotPasswordSchema),
		lastResult: forgotPassword.data?.result,
		onValidate({ formData }) {
			return parseWithZod(formData, { schema: ForgotPasswordSchema })
		},
		// 'onInput' per ADR-101: blur-time revalidation shifts the submit
		// button mid-click and can lose the click.
		shouldRevalidate: 'onInput',
	})

	return (
		<SplitAuthLayout sidePanel={<BrandPanel tagline="Sneller weer aan het oefenen." />}>
			<div className="flex flex-col gap-8">
				<div className="flex flex-col items-center gap-2 text-center">
					<h1 className="text-2xl font-bold text-white">Wachtwoord vergeten</h1>
					<p className="text-sm text-text-muted">
						Geen zorgen, we sturen je een herstelcode.
					</p>
				</div>

				<forgotPassword.Form
					method="POST"
					{...getFormProps(form)}
					className="space-y-2"
				>
					<HoneypotInputs />
					<Field
						labelProps={{
							htmlFor: fields.usernameOrEmail.id,
							children: 'Gebruikersnaam of e-mailadres',
						}}
						inputProps={{
							autoFocus: true,
							...getInputProps(fields.usernameOrEmail, { type: 'text' }),
						}}
						errors={fields.usernameOrEmail.errors}
					/>
					<ErrorList errors={form.errors} id={form.errorId} />
					<StatusButton
						className="w-full"
						status={
							forgotPassword.state === 'submitting'
								? 'pending'
								: (form.status ?? 'idle')
						}
						type="submit"
						disabled={forgotPassword.state !== 'idle'}
					>
						Wachtwoord herstellen
					</StatusButton>
				</forgotPassword.Form>

				<Link
					to="/login"
					className="text-center text-sm font-semibold text-text-muted underline-offset-4 hover:text-white hover:underline"
				>
					← Terug naar inloggen
				</Link>
			</div>
		</SplitAuthLayout>
	)
}

export function ErrorBoundary() {
	return <GeneralErrorBoundary />
}
